import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { DatePickerComponent } from '@app/partials/form/date-picker/date-picker.component';
import { ButtonComponent } from '@app/partials/form/button/button.component';
import { ReportService } from '../shared/report.service';
import { ReportFilters } from '../shared/report.model';
import { VenueService } from '@app/venues/shared/venue.service';
import { VenueModel } from '@app/venues/shared/venue.model';
import { ToastService } from '@app/shared/toast/toast.service';
import { LucideAngularModule, ArrowLeft, CheckCircle } from 'lucide-angular';
import { provideFlatpickrDefaults } from 'angularx-flatpickr';
import { DateUtil } from '@app/shared/utils/date.util';
import { NgClass } from '@angular/common';

@Component({
  selector: 'app-report-create',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    DatePickerComponent,
    ButtonComponent,
    RouterLink,
    LucideAngularModule,
    NgClass,
  ],
  providers: [provideFlatpickrDefaults()],
  templateUrl: './report-create.component.html',
})
export class ReportCreateComponent implements OnInit {
  protected readonly ArrowLeftIcon = ArrowLeft;
  protected readonly CheckIcon = CheckCircle;

  protected type = '';
  protected step: 'filters' | 'confirm' = 'filters';
  protected previewCount: number | null = null;
  protected venues: VenueModel[] = [];
  protected selectedPreset: 'week' | 'month' | 'quarter' | 'year' | null = null;

  protected statusOptions = [
    'Opportunity',
    'Provisional',
    'Confirmed',
    'Cancelled',
    'Cancelled and Transferred',
    'Closed',
  ];

  form = new FormGroup({
    venue_ids: new FormControl<number[]>([], { nonNullable: true }),
    date_from: new FormControl<string | null>(null),
    date_to: new FormControl<string | null>(null),
    statuses: new FormControl<string[]>([], { nonNullable: true }),
  });

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private reportService: ReportService,
    private venueService: VenueService,
    private toastService: ToastService,
  ) {}

  ngOnInit(): void {
    this.type = this.route.snapshot.paramMap.get('type') ?? '';
    this.applyDefaultStatuses();
    this.setPreset('year');
    this.venueService.getAll().subscribe({
      next: (response) => {
        this.venues = response.data as VenueModel[];
        this.selectAllVenues();
      },
      error: () => {
        this.toastService.error('Failed to load venues');
      },
    });
  }

  get reportTitle(): string {
    const map: Record<string, string> = {
      financial_position: 'Financial Position',
      quote_vs_actual: 'Quote vs Actual',
      drinks_packages: 'Drinks Packages',
      product_report: 'Product Report',
      extras_analysis: 'Product Report',
      cancellations: 'Cancellations',
      pizza_offer: 'Pizza Offer',
    };

    return map[this.type] ?? 'Report';
  }

  get isConfirmedOnlyReport(): boolean {
    return ['financial_position', 'quote_vs_actual', 'product_report', 'extras_analysis'].includes(this.type);
  }

  toggleVenue(id: number): void {
    if (!id) {
      return;
    }
    const current = this.form.controls.venue_ids.value;
    if (current.includes(id)) {
      this.form.controls.venue_ids.setValue(current.filter((venueId) => venueId !== id));
      return;
    }

    this.form.controls.venue_ids.setValue([...current, id]);
  }

  toggleStatus(status: string): void {
    if (this.isConfirmedOnlyReport) {
      return;
    }

    const current = this.form.controls.statuses.value;
    if (current.includes(status)) {
      this.form.controls.statuses.setValue(current.filter((value) => value !== status));
      return;
    }

    this.form.controls.statuses.setValue([...current, status]);
  }

  selectAllVenues(): void {
    const ids = this.venues
      .map((venue) => venue.id)
      .filter((id): id is number => id !== null);
    this.form.controls.venue_ids.setValue(ids);
  }

  clearVenues(): void {
    this.form.controls.venue_ids.setValue([]);
  }

  setPreset(range: 'week' | 'month' | 'quarter' | 'year'): void {
    const today = new Date();
    let start: Date;
    let end: Date;

    switch (range) {
      case 'week': {
        const day = today.getDay();
        const diff = day === 0 ? -6 : 1 - day;
        start = new Date(today);
        start.setDate(today.getDate() + diff);
        end = new Date(start);
        end.setDate(start.getDate() + 6);
        break;
      }
      case 'month': {
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        break;
      }
      case 'quarter': {
        const quarterStartMonth = Math.floor(today.getMonth() / 3) * 3;
        start = new Date(today.getFullYear(), quarterStartMonth, 1);
        end = new Date(today.getFullYear(), quarterStartMonth + 3, 0);
        break;
      }
      case 'year': {
        start = new Date(today.getFullYear(), 0, 1);
        end = new Date(today.getFullYear(), 11, 31);
        break;
      }
      default:
        return;
    }

    this.selectedPreset = range;
    this.form.controls.date_from.setValue(DateUtil.parseDateToString(start));
    this.form.controls.date_to.setValue(DateUtil.parseDateToString(end));
  }

  clearPreset(): void {
    this.selectedPreset = null;
    this.form.controls.date_from.setValue(null);
    this.form.controls.date_to.setValue(null);
  }

  continueToPreview(): void {
    const payload = this.buildPayload();

    this.reportService.preview(payload).subscribe({
      next: (response) => {
        this.previewCount = response.data.events_count;
        this.step = 'confirm';
      },
      error: () => {
        this.toastService.error('Failed to preview report');
      },
    });
  }

  generateReport(): void {
    const payload = this.buildPayload();

    this.reportService.createReport(payload).subscribe({
      next: () => {
        this.toastService.success('Report is being generated');
        this.router.navigate(['/reports']);
      },
      error: () => {
        this.toastService.error('Failed to generate report');
      },
    });
  }

  backToFilters(): void {
    this.step = 'filters';
  }

  private buildPayload(): ReportFilters & { type: string } {
    const filters = this.form.getRawValue();

    return {
      type: this.type,
      venue_ids: filters.venue_ids.length ? filters.venue_ids : undefined,
      date_from: filters.date_from || undefined,
      date_to: filters.date_to || undefined,
      statuses: filters.statuses.length ? filters.statuses : undefined,
    };
  }

  private applyDefaultStatuses(): void {
    if (this.isConfirmedOnlyReport) {
      this.form.controls.statuses.setValue(['Confirmed']);

      return;
    }

    this.form.controls.statuses.setValue([...this.statusOptions]);
  }
}
